'use client';

import Link from 'next/link';
import { Lock, Clock, ArrowUpCircle, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/lib/i18n/LanguageContext';
import { formatDateTimeEuropean } from '@/lib/utils/date';

export interface TestNotIncludedNoticeProps {
  /** Shown on list pages (dashboard, My tests) where the event is not obvious. */
  eventTitle?: string;
  eventHref?: string;
  ticketName?: string | null;
  /** Latest close of the open tests; null when a test never closes. */
  openUntil: string | null;
  /** Opens the ticket change modal; omitted when no certificate ticket can be bought now. */
  onUpgrade?: () => void;
  organizerEmail?: string | null;
}

/**
 * "A test is open for this event, but your ticket does not include it."
 * Rendered only while a test is open for a no-certificate ticket (see
 * lib/tests/testNotIncluded), on the event page, My tests and the dashboard.
 */
export default function TestNotIncludedNotice({
  eventTitle,
  eventHref,
  ticketName,
  openUntil,
  onUpgrade,
  organizerEmail,
}: TestNotIncludedNoticeProps) {
  const { t } = useLanguage();

  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50" data-testid="test-not-included-notice">
      <div className="p-4 sm:p-5 flex items-start gap-3">
        <div className="rounded-full bg-amber-100 p-2 flex-shrink-0">
          <Lock className="h-4 w-4 text-amber-700" />
        </div>
        <div className="min-w-0 flex-1">
          {eventTitle && (
            eventHref ? (
              <Link href={eventHref} className="block text-sm font-medium text-gray-500 hover:text-blue-600 hover:underline mb-1 break-words">
                {eventTitle}
              </Link>
            ) : (
              <p className="text-sm font-medium text-gray-500 mb-1 break-words">{eventTitle}</p>
            )
          )}
          <p className="font-semibold text-amber-900">{t('event.test.notIncluded.title')}</p>
          <p className="text-sm text-amber-900/90 mt-1">
            {ticketName
              ? t('event.test.notIncluded.body', { ticket: ticketName })
              : t('event.test.notIncluded.bodyNoTicketName')}{' '}
            {onUpgrade ? t('event.test.notIncluded.upgradeHint') : t('event.test.notIncluded.contactHint')}
          </p>
          {openUntil && (
            <p className="text-xs text-amber-800 mt-2 inline-flex items-center gap-1">
              <Clock className="h-3.5 w-3.5" />
              {t('event.test.notIncluded.openUntil', { date: formatDateTimeEuropean(openUntil) })}
            </p>
          )}
        </div>
      </div>
      {(onUpgrade || organizerEmail) && (
        <div className="px-4 sm:px-5 py-2.5 border-t border-amber-200 flex flex-wrap items-center gap-2">
          {onUpgrade && (
            <Button size="sm" className="text-xs bg-blue-600 hover:bg-blue-700 text-white" onClick={onUpgrade}>
              <ArrowUpCircle className="h-3.5 w-3.5 mr-1" />
              {t('event.test.notIncluded.upgradeButton')}
            </Button>
          )}
          {organizerEmail && (
            <Button asChild size="sm" variant="outline" className="text-xs bg-white">
              <a href={`mailto:${organizerEmail}`}>
                <Mail className="h-3.5 w-3.5 mr-1" />
                {t('event.test.notIncluded.contactButton')}
              </a>
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
